import React, { useState } from 'react';
import { useSearchParams, Link } from 'react-router-dom';
import { Star, CheckCircle } from 'lucide-react';
import axios from 'axios';
import { Button } from '../components/ui/button';
import { useLang } from '../i18n/LanguageContext';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const COPY = {
  fr: { title: 'Votre avis compte', subtitle: 'Notez votre expérience Party LED de 1 à 5 étoiles.', rating: 'Votre note', name: 'Votre nom', email: 'Email (optionnel)', comment: 'Votre avis', commentPh: 'Dites-nous comment vos bracelets LED ont illuminé votre événement…', submit: 'Envoyer mon avis', sending: 'Envoi…', successTitle: 'Merci pour votre avis !', successMsg: 'Votre retour a bien été envoyé. Il sera publié après validation.', back: "Retour à l'accueil", errNoRating: 'Merci de choisir une note.', err: "Envoi impossible, réessayez dans un instant." },
  en: { title: 'Your review matters', subtitle: 'Rate your Party LED experience from 1 to 5 stars.', rating: 'Your rating', name: 'Your name', email: 'Email (optional)', comment: 'Your review', commentPh: 'Tell us how your LED wristbands lit up your event…', submit: 'Send my review', sending: 'Sending…', successTitle: 'Thank you for your review!', successMsg: 'Your feedback has been sent. It will be published after approval.', back: 'Back to home', errNoRating: 'Please pick a rating.', err: 'Could not send, please try again shortly.' },
  ar: { title: 'رأيك يهمّنا', subtitle: 'قيّم تجربتك مع Party LED من 1 إلى 5 نجوم.', rating: 'تقييمك', name: 'اسمك', email: 'البريد الإلكتروني (اختياري)', comment: 'رأيك', commentPh: 'أخبرنا كيف أضاءت أساور LED فعاليتك…', submit: 'إرسال رأيي', sending: 'جارٍ الإرسال…', successTitle: 'شكراً على رأيك!', successMsg: 'تم إرسال رأيك. سيُنشر بعد الموافقة.', back: 'العودة إلى الرئيسية', errNoRating: 'يرجى اختيار تقييم.', err: 'تعذّر الإرسال، حاول بعد قليل.' },
  it: { title: 'La tua opinione conta', subtitle: 'Valuta la tua esperienza Party LED da 1 a 5 stelle.', rating: 'La tua valutazione', name: 'Il tuo nome', email: 'Email (facoltativa)', comment: 'La tua recensione', commentPh: 'Raccontaci come i bracciali LED hanno illuminato il tuo evento…', submit: 'Invia la mia recensione', sending: 'Invio…', successTitle: 'Grazie per la tua recensione!', successMsg: 'Il tuo feedback è stato inviato. Sarà pubblicato dopo approvazione.', back: 'Torna alla home', errNoRating: 'Seleziona una valutazione.', err: 'Invio non riuscito, riprova tra poco.' },
};

const AvisPage = () => {
  const { lang } = useLang();
  const [params] = useSearchParams();
  const c = COPY[lang] || COPY.fr;
  const isRTL = lang === 'ar';
  const slug = params.get('slug') || 'general';

  const [rating, setRating] = useState(0);
  const [hover, setHover] = useState(0);
  const [name, setName] = useState('');
  const [email, setEmail] = useState('');
  const [comment, setComment] = useState('');
  const [sending, setSending] = useState(false);
  const [error, setError] = useState('');
  const [done, setDone] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    if (!rating) { setError(c.errNoRating); return; }
    setError('');
    setSending(true);
    try {
      await axios.post(`${BACKEND_URL}/api/public/reviews`, {
        productSlug: slug, name: name || 'Client', email, rating, comment,
      });
      setDone(true);
    } catch (err) {
      setError(c.err);
    } finally {
      setSending(false);
    }
  };

  return (
    <div className="min-h-screen bg-[#050505] pt-24 pb-16 px-6" dir={isRTL ? 'rtl' : 'ltr'}>
      <div className="max-w-xl mx-auto">
        {done ? (
          <div className="text-center bg-gray-900 border border-gray-800 rounded-2xl p-10" data-testid="avis-success">
            <CheckCircle className="w-14 h-14 text-green-500 mx-auto mb-4" />
            <h1 className="text-2xl font-bold text-white mb-2">{c.successTitle}</h1>
            <p className="text-gray-400 mb-6">{c.successMsg}</p>
            <Link to="/"><Button className="bg-purple-600 hover:bg-purple-700" data-testid="avis-home-btn">{c.back}</Button></Link>
          </div>
        ) : (
          <form onSubmit={submit} className="bg-gray-900 border border-gray-800 rounded-2xl p-8" data-testid="avis-form">
            <h1 className="text-3xl font-bold text-white mb-2">{c.title}</h1>
            <p className="text-gray-400 mb-6">{c.subtitle}</p>

            <label className="block text-sm text-gray-300 mb-2">{c.rating}</label>
            <div className="flex gap-2 mb-6" data-testid="avis-stars">
              {[1, 2, 3, 4, 5].map((n) => (
                <button
                  type="button"
                  key={n}
                  onClick={() => setRating(n)}
                  onMouseEnter={() => setHover(n)}
                  onMouseLeave={() => setHover(0)}
                  data-testid={`avis-star-${n}`}
                  className="transition-transform hover:scale-110"
                  aria-label={`${n}/5`}
                >
                  <Star className={`w-9 h-9 ${n <= (hover || rating) ? 'text-amber-400 fill-amber-400' : 'text-gray-600'}`} />
                </button>
              ))}
            </div>

            <label className="block text-sm text-gray-300 mb-2">{c.name}</label>
            <input value={name} onChange={(e) => setName(e.target.value)} className="w-full bg-gray-800 border border-gray-700 text-white rounded-lg px-3 py-2.5 mb-4 focus:outline-none focus:border-purple-500" data-testid="avis-name" />

            <label className="block text-sm text-gray-300 mb-2">{c.email}</label>
            <input type="email" value={email} onChange={(e) => setEmail(e.target.value)} className="w-full bg-gray-800 border border-gray-700 text-white rounded-lg px-3 py-2.5 mb-4 focus:outline-none focus:border-purple-500" data-testid="avis-email" />

            <label className="block text-sm text-gray-300 mb-2">{c.comment}</label>
            <textarea value={comment} onChange={(e) => setComment(e.target.value)} rows={4} placeholder={c.commentPh} className="w-full bg-gray-800 border border-gray-700 text-white rounded-lg px-3 py-2.5 mb-4 focus:outline-none focus:border-purple-500" data-testid="avis-comment" />

            {error && <p className="text-red-400 text-sm mb-4" data-testid="avis-error">{error}</p>}

            <Button type="submit" disabled={sending} className="w-full bg-purple-600 hover:bg-purple-700 py-4 text-lg" data-testid="avis-submit">
              {sending ? c.sending : c.submit}
            </Button>
          </form>
        )}
      </div>
    </div>
  );
};

export default AvisPage;
